import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { safeSubscribe } from "@/services/ios-safari-fallback";
import type { ProfileRow } from "@/types";
import type { AppState, StoreContextType, LocalizationPrefs, NotificationPrefs, PrivacyPrefs } from "./types";

const StoreContext = createContext<StoreContextType | null>(null);

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  
  // Core state
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [localization, setLocalization] = useState<LocalizationPrefs>({
    timezone: "UTC",
    language: "en",
    currency: "usd",
  });
  const [notifications, setNotifications] = useState<NotificationPrefs>({
    email_notifications: true,
    notify_on_claim: true,
    notify_on_completion: true,
  });
  const [privacy, setPrivacy] = useState<PrivacyPrefs>({
    public_earnings: false,
    public_stats: true,
  });
  const [theme, setThemeState] = useState<"light" | "dark" | "system">(() => {
    // Load theme from localStorage on initialization
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system') {
        return savedTheme;
      }
    }
    return "system";
  });

  // Wrap setTheme to also save to localStorage
  const setTheme = useCallback((newTheme: "light" | "dark" | "system") => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  }, []);

  // Load profile and preferences when user changes
  useEffect(() => {
    if (!user || !user.id) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        // First, try to load from localStorage for immediate display
        const cachedProfile = localStorage.getItem(`profile_${user.id}`);
        if (cachedProfile) {
          const parsedProfile = JSON.parse(cachedProfile);
          setProfile(parsedProfile as ProfileRow);
        }

        // Then fetch fresh data from database
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (error) throw error;
        
        // Subscription expiry is handled server-side (protected entitlement
        // fields reject direct client writes); the profile row is authoritative.
        
        // Update state with fresh data
        setProfile(data as ProfileRow);
        
        // Cache the profile in localStorage for persistence
        localStorage.setItem(`profile_${user.id}`, JSON.stringify(data));
        
        // Extract preferences from profile
        if (data) {
          setLocalization({
            timezone: data.timezone || "UTC",
            language: data.language || "en",
            currency: data.currency || "usd",
          });
          setNotifications({
            email_notifications: data.email_notifications ?? true,
            notify_on_claim: data.notify_on_claim ?? true,
            notify_on_completion: data.notify_on_completion ?? true,
          });
          setPrivacy({
            public_earnings: data.public_earnings ?? false,
            public_stats: data.public_stats ?? true,
          });
        }
      } catch (error: any) {
        // If JWT expired or unauthorized, try to refresh the session
        if (error?.code === 'PGRST303' || error?.message?.includes('JWT') || error?.message?.includes('401')) {
          console.warn("Session expired, attempting to refresh session...");
          
          // Try to refresh the session
          try {
            const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              console.warn("Session refresh failed, clearing profile:", refreshError.message);
              setProfile(null);
              setProfileLoading(false);
              return;
            }
            
            if (refreshedSession) {
              console.log("Session refreshed successfully, retrying profile load...");
              // Retry loading profile with fresh session
              const { data: retryData, error: retryError } = await supabase
                .from("profiles")
                .select("*")
                .eq("id", user.id)
                .single();
              
              if (retryError) {
                console.error("Profile load failed after session refresh:", retryError);
                setProfile(null);
                setProfileLoading(false);
                return;
              }
              
              setProfile(retryData as ProfileRow);
              localStorage.setItem(`profile_${user.id}`, JSON.stringify(retryData));
              
              // Extract preferences from profile
              if (retryData) {
                setLocalization({
                  timezone: retryData.timezone || "UTC",
                  language: retryData.language || "en",
                  currency: retryData.currency || "usd",
                });
                setNotifications({
                  email_notifications: retryData.email_notifications ?? true,
                  notify_on_claim: retryData.notify_on_claim ?? true,
                  notify_on_completion: retryData.notify_on_completion ?? true,
                });
                setPrivacy({
                  public_earnings: retryData.public_earnings ?? false,
                  public_stats: retryData.public_stats ?? true,
                });
              }
            } else {
              console.warn("No session after refresh, clearing profile");
              setProfile(null);
              setProfileLoading(false);
            }
          } catch (refreshErr: any) {
            console.warn("Session refresh error:", refreshErr.message);
            setProfile(null);
            setProfileLoading(false);
          }
          return;
        }
        
        console.error("Failed to load profile:", error);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();

    // Subscribe to real-time profile updates
    const channel = supabase
      .channel("store-profile-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          const newData = payload.new as Partial<ProfileRow>;
          setProfile((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, ...newData };
            // Cache updated profile to localStorage
            localStorage.setItem(`profile_${user.id}`, JSON.stringify(updated));
            return updated;
          });
          
          // Update preferences if they changed
          if (newData.timezone || newData.language || newData.currency) {
            setLocalization({
              timezone: newData.timezone || localization.timezone,
              language: newData.language || localization.language,
              currency: newData.currency || localization.currency,
            });
          }
          if (newData.email_notifications !== undefined || newData.notify_on_claim !== undefined) {
            setNotifications({
              email_notifications: newData.email_notifications ?? notifications.email_notifications,
              notify_on_claim: newData.notify_on_claim ?? notifications.notify_on_claim,
              notify_on_completion: newData.notify_on_completion ?? notifications.notify_on_completion,
            });
          }
          if (newData.public_earnings !== undefined || newData.public_stats !== undefined) {
            setPrivacy({
              public_earnings: newData.public_earnings ?? privacy.public_earnings,
              public_stats: newData.public_stats ?? privacy.public_stats,
            });
          }
        }
      )
      .subscribe((status: string) => {
          if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            console.warn("⚠️ Realtime connection failed - iOS Safari mode activated");
          }
        });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Update functions
  const updateProfile = useCallback(async (updates: Partial<ProfileRow>) => {
    if (!user || !profile) return;
    
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id);

    if (error) throw error;
  }, [user, profile]);

  const updateLocalization = useCallback(async (updates: Partial<LocalizationPrefs>) => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id);

    if (error) throw error;
    
    setLocalization((prev) => ({ ...prev, ...updates }));
  }, [user]);

  const updateNotifications = useCallback(async (updates: Partial<NotificationPrefs>) => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id);

    if (error) throw error;
    
    setNotifications((prev) => ({ ...prev, ...updates }));
  }, [user]);

  const updatePrivacy = useCallback(async (updates: Partial<PrivacyPrefs>) => {
    if (!user) return;
    
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id);

    if (error) throw error;
    
    setPrivacy((prev) => ({ ...prev, ...updates }));
  }, [user]);

  const refetchProfile = useCallback(async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    if (error) throw error;
    setProfile(data as ProfileRow);
    
    // Cache the refreshed profile to localStorage
    localStorage.setItem(`profile_${user.id}`, JSON.stringify(data));
  }, [user]);

  const value: StoreContextType = {
    profile,
    profileLoading,
    localization,
    notifications,
    privacy,
    theme,
    updateProfile,
    updateLocalization,
    updateNotifications,
    updatePrivacy,
    setTheme,
    refetchProfile,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};